import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

import PaymentAmount from './paymentAmount';
import { isNativeApp } from '../../lib/nativeApp';

// localStorage key used to resume a pending PayPal payment if this view is remounted/reloaded
// while the buyer is away on PayPal (a backgrounded PWA may be evicted by iOS).
const PENDING_KEY = 'paypalPendingPayment';

@observer
class PaypalGeneric extends Component {
    constructor(props) {
        super(props);
        this.state = {
            currentPayment: null,
            paymentInProgress: false,
            errorMessage: null,
            waitingForApproval: false,
            redirectUrl: null,
        };
        this.settled = false;
        this.tracking = false;
        this.checking = false;
        this.unmounted = false;
        this.currentPaymentUuidv4 = null;
        this.isNative = isNativeApp();
    }

    isPwaStandalone = () => {
        return window.navigator.standalone === true
            || window.matchMedia('(display-mode: standalone)').matches;
    }

    // Contexts where PayPal cannot hand the buyer back to this very session: an installed PWA on
    // iOS and the Cordova store apps both send them to the system browser. Those land on the
    // neutral payment-done page; this session reconciles by polling when it becomes visible again.
    isSessionlessReturn = () => this.isPwaStandalone() || this.isNative;

    persistPending = (paymentUuidv4) => {
        try {
            window.localStorage.setItem(PENDING_KEY, JSON.stringify({
                paymentUuidv4,
                raceClassContestant: this.props.raceClassContestant || null,
                sale: this.props.sale || null,
                ts: Date.now(),
            }));
        } catch (err) {
            // localStorage may be unavailable (private mode) — in-session polling still works.
        }
    }

    readPending = () => {
        try {
            const raw = window.localStorage.getItem(PENDING_KEY);
            return raw ? JSON.parse(raw) : null;
        } catch (err) {
            return null;
        }
    }

    clearPending = () => {
        try {
            window.localStorage.removeItem(PENDING_KEY);
        } catch (err) {
            // ignore
        }
    }

    // Pre-create the PayPal order so the pay button can be a real <a href> the buyer taps in a
    // SINGLE gesture. The old flow awaited order-create and then called window.open(), which
    // browsers block as an unsolicited popup — the buyer never reached PayPal and the signup's
    // 20-minute payment reservation quietly ran out.
    createPayment = async () => {
        const { currency, amount, title, type, customer, race, raceClassContestant, sale } = this.props;
        const { paypalStore } = this.props.stores;

        if (!amount || amount <= 0) {
            return;
        }
        // A race signup payment without its contestant would be created as a plain payment and
        // captured with nothing to approve. The backend refuses it too; don't even ask.
        const isRaceSignupPayment = ['signupfee', 'signup', 'rest', 'seeding'].includes(type) || race > 0;
        if (isRaceSignupPayment && !(raceClassContestant > 0)) {
            this.setState({ paymentInProgress: false, errorMessage: 'payment.paypal-missing-signup' });
            return;
        }

        this.setState({ paymentInProgress: true, errorMessage: null });
        this.settled = false;
        this.tracking = false;

        // PayPal always returns into the tab it owns, never into this one, so send it to a
        // neutral public page rather than the authenticated checkout.
        const wantedPage = `${window.location.href.split('#')[0]}#/window/${
            this.isSessionlessReturn() ? 'payment-done' : 'close'
        }`;

        try {
            const response = await paypalStore.requestPayment({
                currency,
                amount,
                title,
                type,
                customer,
                race,
                raceClassContestant,
                sale,
                wantedPage,
            });

            if (response?.status !== 200 || !response?.data?.redirectUrl) {
                this.setState({
                    paymentInProgress: false,
                    errorMessage: 'payment.paypal-create-failed',
                });
                return;
            }

            const { paymentUuidv4, redirectUrl } = response.data;
            this.currentPaymentUuidv4 = paymentUuidv4;

            // Tracking and the parent "payment started" signal begin only when the buyer actually
            // taps through to PayPal — see handleOpenPaypal — so we never poll an order nobody
            // opened, nor hide the payment-method selector prematurely.
            this.setState({
                paymentInProgress: false,
                redirectUrl,
            });
        } catch (err) {
            console.error('[PaypalGeneric] Payment error:', err);
            this.setState({
                paymentInProgress: false,
                errorMessage: 'payment.paypal-create-failed',
            });
        }
    }

    // Fired by the buyer's tap. The <a href> navigation proceeds on its own; here we only signal
    // the parent that payment has started and begin tracking so we can reconcile on return.
    handleOpenPaypal = () => {
        const { callback = () => {} } = this.props;
        if (!this.currentPaymentUuidv4) {
            return;
        }
        callback({ paymentUuidv4: this.currentPaymentUuidv4 });
        this.tracking = true;
        this.persistPending(this.currentPaymentUuidv4);
        this.setState({ waitingForApproval: true });
        this.startPolling(this.currentPaymentUuidv4);
    }

    startPolling = (paymentUuidv4) => {
        const { paypalStore } = this.props.stores;
        this.currentPaymentUuidv4 = paymentUuidv4;

        paypalStore.pollPaymentStatus(paymentUuidv4, {
            onUpdate: (data) => {
                if (data?.paymentStatus >= 1) {
                    this.setState({ currentPayment: data });
                }
            },
            onComplete: data => this.finalizePayment(data),
            onFailed: data => this.failPayment(data),
            shouldStop: () => this.unmounted || this.settled,
            // Cover the signup's 20-minute payment reservation rather than giving up after three
            // minutes: 3s ramping to 15s is ~21 minutes, so a slow buyer is still reconciled here
            // instead of having to rely on the hourly job.
            maxAttempts: 120,
            interval: 3000,
            maxInterval: 15000,
        });
    }

    // Re-check immediately instead of waiting for the (possibly frozen) timer: mobile browsers
    // suspend setTimeout while the tab is backgrounded during the PayPal hop.
    checkPaymentNow = async () => {
        if (this.settled || !this.tracking || !this.currentPaymentUuidv4 || this.checking) {
            return;
        }
        this.checking = true;
        try {
            const { paypalStore } = this.props.stores;
            const response = await paypalStore.checkPaymentStatus(this.currentPaymentUuidv4);
            const status = response?.data?.paymentStatus;
            if (status === 3) {
                await this.finalizePayment(response.data);
            } else if (status >= 90) {
                this.failPayment(response.data);
            } else if (response?.status === 404) {
                this.failPayment({ cancelled: true });
            } else if (status >= 1) {
                this.setState({ currentPayment: response.data });
            }
        } finally {
            this.checking = false;
        }
    }

    finalizePayment = async (data) => {
        if (this.settled) {
            return;
        }
        this.settled = true;
        this.clearPending();

        const { currency, amount, title, type, race, raceClassContestant, sale } = this.props;
        const { paymentStore } = this.props.stores;
        const uuidv4 = this.currentPaymentUuidv4;

        const updatedPayments = await paymentStore.load({ query: { uuidv4 }, skipUpdate: true });
        const updatedPayment = updatedPayments?.[0];

        this.setState({
            waitingForApproval: false,
            redirectUrl: null,
            currentPayment: updatedPayment || { ...data },
        });

        const { callbackPaymentDone } = this.props;
        if (callbackPaymentDone) {
            await callbackPaymentDone({
                currency,
                amount,
                title,
                type,
                paymentUuidv4: uuidv4,
                paymentStatus: data?.paymentStatus,
                race,
                raceClassContestant,
                currentPayment: updatedPayment,
                sale,
            });
        }
    }

    failPayment = (data) => {
        if (this.settled) {
            return;
        }
        this.settled = true;
        this.clearPending();
        this.setState({
            waitingForApproval: false,
            redirectUrl: null,
            errorMessage: data?.timeout
                ? 'payment.paypal-payment-timeout'
                : 'payment.paypal-payment-cancelled',
        });
    }

    handleVisibility = () => {
        if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
            return;
        }
        this.checkPaymentNow();
    }

    componentDidMount() {
        const { paypalStore } = this.props.stores;
        paypalStore.updateKeyValue('redirectUrl', null);

        document.addEventListener('visibilitychange', this.handleVisibility);
        window.addEventListener('focus', this.handleVisibility);

        // Resume a payment left pending by a remount / cold reload of the same checkout.
        let resumed = false;
        const pending = this.readPending();
        const fresh = pending && (!pending.ts || (Date.now() - pending.ts) < 25 * 60 * 1000);
        const sameTarget = pending
            && (!pending.raceClassContestant || !this.props.raceClassContestant
                || pending.raceClassContestant === this.props.raceClassContestant)
            && (!pending.sale || !this.props.sale || pending.sale === this.props.sale);
        if (pending?.paymentUuidv4 && fresh && sameTarget) {
            this.settled = false;
            this.tracking = true;
            this.setState({ waitingForApproval: true });
            this.startPolling(pending.paymentUuidv4);
            this.checkPaymentNow();
            resumed = true;
        } else if (pending && (!fresh || !sameTarget)) {
            this.clearPending();
        }

        if (!resumed) {
            this.createPayment();
        }
    }

    componentWillUnmount() {
        this.unmounted = true;
        document.removeEventListener('visibilitychange', this.handleVisibility);
        window.removeEventListener('focus', this.handleVisibility);
    }

    render() {
        const { amount, title, currency } = this.props;
        const { currentPayment = {}, paymentInProgress, errorMessage, waitingForApproval, redirectUrl } = this.state;
        const sessionless = this.isSessionlessReturn();

        const progress = {
            0: 0,
            1: 33,
            2: 66,
            3: 100,
        };
        // Clamp to the real progress range — terminal statuses (90/91/99) must NOT light up the
        // bar (91 >= 3 would otherwise read as "Paid").
        const ps = currentPayment?.paymentStatus;
        const progressStatus = (typeof ps === 'number' && ps >= 0 && ps <= 3) ? ps : 0;

        return (
			<div class='w-100 d-flex justify-content-center'>
                <div class='w-100 h-100' style='max-width: 960px; padding-top: 20px; padding-bottom: 20px;'>

                    <div class='d-flex flex-row justify-content-center mt-0'>
                        <PaymentAmount
                            stores={this.props.stores}
                            title={title}
                            amount={amount}
                            currency={currency}
                        />
                    </div>

                    {ps === 3 ? <>
                        <div class='d-flex flex-column'>
                            <div class='d-flex flex-row justify-content-center display-1 text-success'>
                                <i class='fa-duotone fa-check fa-beat' />
                            </div>
                            <div class='d-flex flex-row justify-content-center mt-3'>
                                <nobr><Text id='payment.paypal-payment-done'>Payment is done.</Text></nobr>
                            </div>
                        </div>
                    </> : <>
                        {errorMessage ? <>
                            <div class='d-flex flex-column align-items-center mt-3'>
                                <div class='display-1 mb-3 text-danger'>
                                    <i class='fa-duotone fa-circle-xmark' />
                                </div>
                                <div class='alert alert-danger text-center w-100'>
                                    <Text id={errorMessage}>Something went wrong. Please try again.</Text>
                                </div>
                                <button
                                    type='button'
                                    class='btn btn-primary rounded-pill'
                                    onClick={this.createPayment}
                                >
                                    <nobr><i class='fa-duotone fa-credit-card' /> <Text id='payment.pay-with-paypal'>Pay with PayPal</Text></nobr>
                                </button>
                            </div>
                        </> : redirectUrl ? <>
                            <div class='d-flex flex-column align-items-center mt-3'>
                                <p class='fs-5 text-center'>
                                    <Text id='payment.paypal-open-instruction'>Tap the button to open PayPal and pay.</Text>
                                </p>
                                <a
                                    href={redirectUrl}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    class='btn btn-primary rounded-pill'
                                    onClick={this.handleOpenPaypal}
                                >
                                    <nobr><i class='fa-duotone fa-credit-card' /> <Text id='payment.pay-with-paypal'>Pay with PayPal</Text></nobr>
                                </a>
                                <p class='text-body-secondary text-center mt-3'>
                                    {sessionless
                                        ? <Text id='payment.paypal-return-when-done'>Come back here when you are done.</Text>
                                        : <Text id='payment.paypal-keep-this-tab'>Keep this tab open — it updates when PayPal is done.</Text>}
                                </p>
                            </div>
                        </> : waitingForApproval ? <>
                            <div class='d-flex flex-column align-items-center mt-3'>
                                <div class='display-1 mb-3'>
                                    <i class='fa-duotone fa-spinner-third fa-spin' />
                                </div>
                                <p class='text-body-secondary text-center'>
                                    <Text id='payment.paypal-checking'>Checking payment status...</Text>
                                </p>
                            </div>
                        </> : paymentInProgress ? <>
                            <div class='d-flex flex-column align-items-center mt-3'>
                                <div class='display-1 mb-3'>
                                    <i class='fa-duotone fa-spinner-third fa-spin' />
                                </div>
                                <p class='text-body-secondary'>
                                    <Text id='payment.paypal-preparing'>Preparing payment...</Text>
                                </p>
                            </div>
                        </> : <>
                            <div class='d-flex flex-column align-items-center mt-0'>
                                <button
                                    type='button'
                                    class='btn btn-primary rounded-pill'
                                    onClick={this.createPayment}
                                >
                                    <nobr><i class='fa-duotone fa-credit-card' /> <Text id='payment.pay-with-paypal'>Pay with PayPal</Text></nobr>
                                </button>
                            </div>
                        </>}
                    </>}

                    <div class='d-flex flex-row mt-5'>
                        <div class='progress-bar-container'>
                            <div class='progress-label-container'>
                                <div class='progress-label'>
                                    <div class='progress-label-text'>Start</div>
                                </div>
                                <div class='progress-label'>
                                    <div class='progress-label-text'>PayPal</div>
                                </div>
                                <div class='progress-label'>
                                    <div class='progress-label-text'>Approved</div>
                                </div>
                                <div class='progress-label'>
                                    <div class='progress-label-text'>Paid</div>
                                </div>
                            </div>
                            <div class='progress'>
                                <div class='progress-bar' style={`width: ${progress[progressStatus]}%`} aria-valuenow='25' aria-valuemin='0' aria-valuemax='100'></div>
                            </div>
                            <div class='progress-dot-container'>
                                <div class={`progress-dot ${progressStatus >= 0 ? 'active' : ''}`}>&nbsp;</div>
                                <div class={`progress-dot ${progressStatus >= 1 ? 'active' : ''}`}>&nbsp;</div>
                                <div class={`progress-dot ${progressStatus >= 2 ? 'active' : ''}`}>&nbsp;</div>
                                <div class={`progress-dot ${progressStatus >= 3 ? 'active' : ''}`}>&nbsp;</div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        );
    }
}

export default PaypalGeneric;
